import { BattleScene } from "./BattleScene";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { BoardContainer } from "./board-container/BoardContainer";
import { GameManager } from "../../GameManager";
import enemiesData from "../../../assets/config/enemies.json";
import turretsData from "../../../assets/config/turrets.json";
import wavesData from "../../../assets/config/waves.json";
import * as Creepts from "endless-siege-engine";
import { TutorialManager } from "./TutorialManager";

export class BattleManager {

    public static engine: Creepts.Engine;

    public static init(): void {

        const aspectRatio = window.innerHeight / window.innerWidth;

        if (aspectRatio > 1.5) {
            if (GameVars.currentMapData.size.c > 11) {
                GameVars.scaleCorrectionFactor = 1;
            } else if (GameVars.currentMapData.size.c > 10) {
                GameVars.scaleCorrectionFactor = 1.1;
            } else {
                GameVars.scaleCorrectionFactor = 1.2;
            }
        }

        let gameConfig: Creepts.GameConfig;

        if (GameVars.currentScene === BattleScene.currentInstance) {
            GameVars.enemiesPathCells = GameVars.currentMapData.path;
            GameVars.plateausCells = GameVars.currentMapData.plateaus;

            GameVars.enemiesData = enemiesData.enemies;
            GameVars.turretsData = turretsData.turrets;
            GameVars.wavesData = wavesData.waves;

            gameConfig = {
                timeStep: GameConstants.TIME_STEP,
                runningInClientSide: true,
                enemySpawningDeltaTicks: GameConstants.ENEMY_SPAWNING_DELTA_TICKS,
                credits: GameConstants.DEVELOPMENT ? 5e5 : GameConstants.INITIAL_CREDITS,
                lifes: GameConstants.INITIAL_LIFES,
                boardSize: GameVars.currentMapData.size,
                enemiesPathCells : GameVars.enemiesPathCells,
                plateausCells: GameVars.plateausCells
            };

            GameVars.logsObject = {
                actions: []
            };
        } else {
            GameVars.enemiesPathCells = GameVars.levelObject.gameConfig.enemiesPathCells;
            GameVars.plateausCells = GameVars.levelObject.gameConfig.plateausCells;

            GameVars.enemiesData = GameVars.levelObject.enemiesData;
            GameVars.turretsData = GameVars.levelObject.turretsData;
            GameVars.wavesData = GameVars.levelObject.wavesData;

            gameConfig = GameVars.levelObject.gameConfig;
        }

        if (GameVars.currentScene === BattleScene.currentInstance || !GameVars.timeStepFactor) {
            GameVars.timeStepFactor = 1;
        }
        
        GameVars.currentWave = 1;
        GameVars.paused = false;
        GameVars.semiPaused = false;
        GameVars.waveOver = true;
        GameVars.autoSendWave = false;
        GameVars.loopNumber = 1;
        GameVars.loopRate = 1;
        GameVars.loopVolume = .2;
        GameVars.dangerRate = 1;
        GameVars.gameOver = false;

        BattleManager.engine = new Creepts.Engine(gameConfig, GameVars.enemiesData, GameVars.turretsData, GameVars.wavesData);

        BattleManager.setTimeStepFactor(GameVars.timeStepFactor);

        if (GameVars.currentScene === BattleScene.currentInstance) {
            GameVars.levelObject = {
                engineVersion: BattleManager.engine.version,
                gameConfig: gameConfig,
                enemiesData: GameVars.enemiesData,
                turretsData: GameVars.turretsData,
                wavesData: GameVars.wavesData
            };
        }
        
        BattleManager.engine.addEventListener(Creepts.Event.ENEMY_SPAWNED, BattleManager.onEnemySpawned, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.ENEMY_REACHED_EXIT, BattleManager.onEnemyReachedExit, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.BULLET_SHOT, BattleManager.onBulletShot, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.GLUE_BULLET_SHOT, BattleManager.onGlueBulletShot, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.ENEMY_HIT, BattleManager.onEnemyHit, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.REMOVE_BULLET, BattleManager.removeBullet, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.REMOVE_GLUE_BULLET, BattleManager.removeGlueBullet, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.ENEMY_GLUE_HIT, BattleManager.onEnemyGlueHit, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.ENEMY_KILLED, BattleManager.onEnemyKilled, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.LASER_SHOT, BattleManager.onLaserBeamShot, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.MORTAR_SHOT, BattleManager.onMortarShot, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.GLUE_SHOT, BattleManager.onGlueShot, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.MINE_SHOT, BattleManager.onMineShot, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.GLUE_CONSUMED, BattleManager.onGlueConsumed, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.ENEMIES_TELEPORTED, BattleManager.onEnemiesTeleported, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.NO_ENEMIES_ON_STAGE, BattleManager.onNoEnemiesOnStage, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.WAVE_OVER, BattleManager.onWaveOver, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.GAME_OVER, BattleManager.onGameOver, BattleManager);
        BattleManager.engine.addEventListener(Creepts.Event.ACTIVE_NEXT_WAVE, BattleManager.onNextWaveActive, BattleManager);
    }

    public static update(time: number, delta: number): void {

        BattleManager.engine.update();
    }

    public static pause(): void {

        GameVars.currentScene.anims.pauseAll();

        GameVars.paused = true;
    }

    public static resume(): void {

        GameVars.currentScene.anims.resumeAll();

        GameVars.paused = false;
    }

    public static semiPause(): void {

        GameVars.currentScene.anims.pauseAll();

        GameVars.semiPaused = true;
    }

    public static semiResume(): void {

        GameVars.currentScene.anims.resumeAll();

        GameVars.semiPaused = false;
    }

    public static setTimeStepFactor(timeStepFactor: number): void {

        GameVars.timeStepFactor = timeStepFactor;

        BattleManager.engine.timeStep = GameConstants.TIME_STEP / timeStepFactor;
    }

    public static newWave(): number {

        const bonus = BattleManager.engine.newWave();

        if (bonus !== null) {

            GameVars.waveOver = false;

            BattleScene.currentInstance.newWave(bonus);

            const action = {type: GameConstants.TYPE_NEXT_WAVE, tick: BattleManager.engine.ticksCounter};
            BattleManager.addAction(action);
        }

        return bonus;
    }

    public static createTurret(type: string): void {

        if (GameVars.currentScene === BattleScene.currentInstance) {
            BattleScene.currentInstance.createTurret(type);
        }
    }

    public static addTurretToScene(type: string, position: {r: number, c: number}): void {

        BoardContainer.currentInstance.addTurret(type, position);
    }

    public static addTurret(type: string, position: {r: number, c: number}): Creepts.Turret {

        let data = BattleManager.engine.addTurret(type, position);

        if (data.turret) {
            let action = {type: GameConstants.TYPE_ADD_TURRET, tick: BattleManager.engine.ticksCounter, turretType: data.turret.type, position: position};
            BattleManager.addAction(action);
        }

        return data.turret;
    }

    public static sellTurret(id: number): void {

        if (BattleManager.engine.sellTurret(id).success) {

            let action = {type: GameConstants.TYPE_SELL_TURRET, tick: BattleManager.engine.ticksCounter, id: id};
            BattleManager.addAction(action);

            BoardContainer.currentInstance.onTurretSold(id);
        }
    }

    public static onClickMenu(): void {

        BoardContainer.currentInstance.showPauseMenu();
    }

    public static setAutoSendWave(value: boolean): void {

        GameVars.autoSendWave = value;
    }

    public static improveTurret(id: number): void {

        const success = BattleManager.engine.improveTurret(id).success;

        if (success) {
            let action = {type: GameConstants.TYPE_LEVEL_UP_TURRET, tick: BattleManager.engine.ticksCounter, id: id};
            BattleManager.addAction(action);

            BoardContainer.currentInstance.improveTurret(id);
        }
    }

    public static upgradeTower(id: number): void {
        
        const success = BattleManager.engine.upgradeTurret(id).success;

        if (success) {

            let action = {type: GameConstants.TYPE_UPGRADE_TURRET, tick: BattleManager.engine.ticksCounter, id: id};
            BattleManager.addAction(action);

            BoardContainer.currentInstance.upgradeTurret(id);
            
            if (GameVars.currentScene === BattleScene.currentInstance) {
                BattleScene.currentInstance.updateTurretMenu();
            }
        }
    }

    public static setNextStrategy(id: number): void {

        if (BattleManager.engine.setNextStrategy(id).success) {
            let action = {type: GameConstants.TYPE_CHANGE_STRATEGY_TURRET, tick: BattleManager.engine.ticksCounter, id: id};
            BattleManager.addAction(action);
        }
    }

    public static setFixedTarget(id: number): void {

        if (BattleManager.engine.setFixedTarget(id).success) {
            let action = {type: GameConstants.TYPE_CHANGE_FIXED_TARGET_TURRET, tick: BattleManager.engine.ticksCounter, id: id};
            BattleManager.addAction(action);
        }
    }

    public static createRangeCircle(range: number, x: number, y: number): Phaser.GameObjects.Image {

        return BoardContainer.currentInstance.createRangeCircle(range, x, y);
    }

    public static hideRangeCircles(): void {

        BoardContainer.currentInstance.hideRangeCircles();
    }

    public static showTurretMenu(turret: Creepts.Turret): void {

        BattleScene.currentInstance.showTurretMenu(turret);
    }

    public static hideTurretMenu(): void {

        BattleScene.currentInstance.hideTurretMenu();
    }

    public static setTurretPrePosition(c: number, r: number): void {

        BoardContainer.currentInstance.setTurretPrePosition(c, r);
    }

    private static addAction(action): void {

        GameVars.logsObject.actions.push(action);
    }

    private static onEnemySpawned(enemy: Creepts.Enemy, p: {r: number, c: number} ): void {
        
        BoardContainer.currentInstance.addEnemy(enemy, p);
    }

    private static onEnemyReachedExit(enemy: Creepts.Enemy): void {

        BoardContainer.currentInstance.removeEnemy(enemy.id);

        if (!BattleManager.engine.gameOver) {

            GameVars.dangerRate = Math.max(GameVars.dangerRate - .0125, .75);
            BattleScene.currentInstance.onEnemyReachedExit();
        }
        
        BattleScene.currentInstance.hud.updateLifes();
    }

    private static onBulletShot(bullet: Creepts.Bullet, projectileTurret: Creepts.ProjectileTurret): void {

        BoardContainer.currentInstance.addBullet(bullet, projectileTurret);
    }

    private static onGlueBulletShot(bullet: Creepts.GlueBullet, turret: Creepts.GlueTurret): void {

        BoardContainer.currentInstance.addGlueBullet(bullet, turret);
    }

    private static onLaserBeamShot(laserTurret: Creepts.LaserTurret, enemies: Creepts.Enemy[]): void {

        BoardContainer.currentInstance.addLaserBeam(laserTurret, enemies);
    }

    private static onMortarShot(mortar: Creepts.Mortar, launchTurret: Creepts.LaunchTurret): void {

        BoardContainer.currentInstance.addMortar(mortar, launchTurret);
    }

    private static onGlueShot(glue: Creepts.Glue, turret: Creepts.GlueTurret): void {

        BoardContainer.currentInstance.addGlue(glue, turret);
    }

    private static onMineShot(mine: Creepts.Mine, launchMine: Creepts.LaunchTurret): void {

        BoardContainer.currentInstance.addMine(mine, launchMine);
    }

    private static onGlueConsumed(glue: Creepts.Glue): void {

        BoardContainer.currentInstance.onGlueConsumed(glue);
    }

    private static onEnemyHit(enemies: Creepts.Enemy[], bullet?: Creepts.Bullet, mortar?: Creepts.Mortar, mine?: Creepts.Mine, fire?: boolean, electric?: boolean): void {

        let impactType: string = null;

        if (fire && electric) {
            impactType = GameConstants.IMPACT_FIRE_ELECTRIC;
        } else if (bullet) {
            impactType = GameConstants.IMPACT_ARROW;
        } else if (mortar) {
            impactType = GameConstants.IMPACT_MORTAR;
        } else if (mine) {
            impactType = GameConstants.IMPACT_MINE;
        } else if (fire) {
            impactType = GameConstants.IMPACT_FIRE;
        } else if (electric) {
            impactType = GameConstants.IMPACT_ELECTRIC;
        } 

        for (let i = 0; i < enemies.length; i ++) {
            BoardContainer.currentInstance.onEnemyHit(enemies[i], impactType);
        }
        
        if (bullet) {
            BoardContainer.currentInstance.removeBullet(bullet);
        } 

        if (mortar) {
            BoardContainer.currentInstance.detonateMortar(mortar);
        }

        if (mine) {
            BoardContainer.currentInstance.detonateMine(mine);
        }
    }

    private static removeBullet(bullet?: Creepts.Bullet) {

        BoardContainer.currentInstance.removeBullet(bullet);
    }

    private static removeGlueBullet(bullet: Creepts.GlueBullet): void {
        
        BoardContainer.currentInstance.removeGlueBullet(bullet);
    }

    private static onEnemyGlueHit(enemies: Creepts.Enemy[], bullet: Creepts.GlueBullet): void {

        for (let i = 0; i < enemies.length; i ++) {
            BoardContainer.currentInstance.onEnemyGlueHit(enemies[i]);
        }

        if (bullet) {
            BoardContainer.currentInstance.removeGlueBullet(bullet);
        } 
    }

    private static onEnemiesTeleported(teleportedEnemiesData: {enemy: Creepts.Enemy, glueTurret: Creepts.GlueTurret} []): void {

        for (let i = 0; i < teleportedEnemiesData.length; i++) {
            BoardContainer.currentInstance.teleportEnemy(teleportedEnemiesData[i].enemy, teleportedEnemiesData[i].glueTurret);
        }
    }

    private static onEnemyKilled(enemy: Creepts.Enemy): void {

        BoardContainer.currentInstance.onEnemyKilled(enemy);

        if (!GameVars.gameData.tutorialSeen && !TutorialManager.isFirstEnemyKilled) {
            TutorialManager.onFirstEnemyKilled();
        }
    }

    private static onNoEnemiesOnStage(): void {

        if (!BattleManager.engine.gameOver) {
            BoardContainer.currentInstance.showRoundCompletedLayer();
        }
    }

    private static onWaveOver(): void {
       
        if (GameVars.autoSendWave) {
            BattleScene.currentInstance.gui.onClickNextWave();
        } else {
            GameVars.waveOver = true;
        }
    }

    private static onNextWaveActive(): void {
        
        if (GameVars.currentScene === BattleScene.currentInstance) {
            BattleScene.currentInstance.gui.activeNextWave();
        }
    }

    private static onGameOver(): void {

        GameVars.gameOver = true;

        if (BattleManager.engine.score > GameVars.gameData.scores[GameVars.currentMapId]) {
            GameVars.gameData.scores[GameVars.currentMapId] = BattleManager.engine.score;
            GameManager.writeGameData();
        }

        BattleScene.currentInstance.showGameOverLayer();   
    }
}
